/**
 * ReferenceStyleRow — the top row of a node's Config/Refine tab: References,
 * a vertical divider, then the Style tile. Shared by ConfigTabContent,
 * VideoConfigTabContent and RefineTabContent so the three stay identical.
 *
 * References sizes to its content and may shrink (`min-w-0`) so its thumbnail
 * strip's `overflow-x-auto` scrolls once it runs out of room; the divider and
 * style tile never shrink, so Style stays visible however many references are
 * attached. The divider only renders when both halves are present.
 */

import { memo, type ReactNode } from 'react'

export interface ReferenceStyleRowProps {
  references?: ReactNode
  style?: ReactNode
  className?: string
}

export const ReferenceStyleRow = memo(function ReferenceStyleRow({
  references,
  style,
  className = '',
}: ReferenceStyleRowProps) {
  return (
    <div className={`flex items-stretch gap-4 ${className}`} data-testid="reference-style-row">
      {references && <div className="min-w-0">{references}</div>}
      {references && style && (
        <div
          aria-hidden="true"
          className="w-px flex-shrink-0 self-stretch bg-border-subtle"
          data-testid="reference-style-divider"
        />
      )}
      {style && <div className="flex-shrink-0">{style}</div>}
    </div>
  )
})

ReferenceStyleRow.displayName = 'ReferenceStyleRow'
